<script>
// @ts-nocheck
import Loading from '@/rex-components/rex-loading.vue';
import NoData from '@/rex-components/rex-no-data.vue';
import TableRowIteratorV2 from '@/rex-components/rex-TableRowIteratorV2.vue';
import { UseVirtualList, vIntersectionObserver } from '@vueuse/components';
import { throttle } from 'lodash';
import { REX_TABLE_INFINITE_SCROLL_THROTTLE_TIME } from '@/rex-components/rex.comp.config';

export default {
  components: {
    Loading,
    NoData,
    TableRowIteratorV2,
    UseVirtualList,
  },
  directives: {
    intersectionObserver: vIntersectionObserver,
  },
  props: {
    headers: { type: Array, required: true },
    items: { type: Array, required: true },
    height: { type: String, default: '40rem' },
    enableDetails: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    hideRows: { type: Boolean, default: false },
    enableScrollFooter: { type: Boolean, default: false },
    dataKey: { type: String, default: '_id' },
    striped: { type: Boolean, default: false },
    enableInfiniteScroll: { type: Boolean, default: false },
    variant: { type: String, default: 'primary' },
    bodyVariant: { type: String, default: 'null' },
    title: { type: String, default: '' },
    showTitle: { type: Boolean, default: false },
    tablewidth: { type: String, default: '' },
    noDataMessage: { type: String, default: '' },
    itemHeight: { type: String, default: '' },
    showSelected: { type: Boolean, default: false },
    selectedIndex: { type: Number, default: 0 },
  },
  computed: {
    showTableRows() {
      return !this.hideRows && this.items?.length > 0;
    },
    getHeaderClass() {
      return this.variant === 'primary' ? 'header-primary' : 'header-secondary';
    },
    getHeaderTdStyle() {
      return this.showTitle ? { borderRadius: '0rem' } : {};
    },
    getComputedRowHeight() {
      // (border + padding height) + font height
      return (
        20 +
        1.5 * parseFloat(getComputedStyle(document.documentElement).fontSize)
      );
    },
  },
  methods: {
    onIntersectionObserve: throttle(function () {
      !this.loading && this.$emit('fetchNextPageData');
    }, REX_TABLE_INFINITE_SCROLL_THROTTLE_TIME),
  },
};
</script>

<template>
  <div class="table-component-container" :style="{ width: tablewidth }">
    <table class="table-container">
      <caption v-if="showTitle" class="title">
        <slot name="table-title">
          {{ title }}
        </slot>
      </caption>
      <thead class="header-container" :class="getHeaderClass">
        <tr class="header-tr">
          <td
            v-for="header in headers"
            :key="header.name"
            :style="{ width: header.width, ...getHeaderTdStyle }"
            class="header-td"
          >
            <slot :name="`header-${header.key}`" :header="header">
              <div
                class="header-slot-default"
                v-tooltip:top.ellipsis="header.name"
              >
                {{ header.name }}
              </div>
            </slot>
          </td>
        </tr>
      </thead>
      <tbody
        class="body-container"
        :style="{ height, overflow: enableInfiniteScroll ? 'hidden' : 'auto' }"
      >
        <div v-if="showTableRows">
          <UseVirtualList
            v-if="enableInfiniteScroll"
            :list="[...items, { dispFooter: true }]"
            :options="{ itemHeight: itemHeight || getComputedRowHeight }"
            :style="{ height }"
            class="virtual-list-box"
            @tobottom="() => $emit('fetchNextPageData')"
            @scroll="(e) => $emit('onScroll', e)"
          >
            <template #default="{ data, index }">
              <TableRowIteratorV2
                :index="index"
                :item="data"
                :headers="headers"
                :striped="striped"
                :enableDetails="enableDetails"
                :variant="bodyVariant ? bodyVariant : variant"
                :showSelected="showSelected"
                :selectedIndex="selectedIndex"
                @rowMouseEnter="(e, i) => $emit('rowMouseEnter', e, i)"
                @rowMouseLeave="(e, i) => $emit('rowMouseLeave', e, i)"
                v-if="!data.dispFooter"
              >
                <template
                  v-for="headerObj in headers"
                  #[`body-${headerObj.key}`]="{ item, index, header }"
                >
                  <slot
                    :name="`body-${header.key}`"
                    :header="header"
                    :item="item"
                    :index="index"
                  ></slot>
                </template>

                <template #row-details="{ item }">
                  <slot name="row-details" :item="item"></slot>
                </template>
              </TableRowIteratorV2>
              <div
                v-if="enableScrollFooter && data.dispFooter"
                v-intersection-observer="onIntersectionObserve"
                :style="{ height: `${getComputedRowHeight}px` }"
              >
                <slot name="infinite-footer">
                  <Loading :smallar="true" class="infinite-loader" />
                </slot>
              </div>
            </template>
          </UseVirtualList>
          <div
            v-for="(item, index) in items"
            :key="index"
            class="row-box"
            v-else
          >
            <TableRowIteratorV2
              :index="index"
              :item="item"
              :headers="headers"
              :striped="striped"
              :enableDetails="enableDetails"
              :variant="bodyVariant ? bodyVariant : variant"
              :showSelected="showSelected"
              :selectedIndex="selectedIndex"
              @rowMouseEnter="(e, i) => $emit('rowMouseEnter', e, i)"
              @rowMouseLeave="(e, i) => $emit('rowMouseLeave', e, i)"
            >
              <template
                v-for="headerObj in headers"
                #[`body-${headerObj.key}`]="{ item, index, header }"
              >
                <slot
                  :name="`body-${header.key}`"
                  :header="header"
                  :item="item"
                  :index="index"
                ></slot>
              </template>

              <template #row-details="{ item }">
                <slot name="row-details" :item="item"></slot>
              </template>
            </TableRowIteratorV2>
          </div>
        </div>
        <div v-else class="no-rows-container">
          <div v-if="loading">
            <Loading />
          </div>
          <div v-else>
            <slot name="no-data">
              <NoData :message="noDataMessage || $t('noData')"></NoData>
            </slot>
          </div>
        </div>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
.table-component-container {
  @include dropshadow-effect();
  width: 100%;
  height: 100%;
  border-radius: 0.688rem;
  overflow: hidden;
}
.table-container {
  width: 100%;
  height: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.header-container {
  height: 3.5rem;
  text-align: left;
  display: table;
  width: 100%;
  table-layout: fixed;
}
.header-primary {
  color: $font-primary;
  font-family: $semi-bold;
  font-size: 1.25rem;
  background-color: $bg-tertiary;
  border-bottom: 0.0125rem solid $border-color-primary;
  border-top-right-radius: 0.7875rem;
  border-top-left-radius: 0.7875rem;
}
.header-secondary {
  font-size: 0.875rem;
  background-color: $bg-secondary;
  color: $table-header-text-grey;
}
.body-container {
  background: $bg-secondary;
  border-bottom-left-radius: 0.688rem;
  border-bottom-right-radius: 0.688rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  width: 100%;
  display: inline-table;
}
.virtual-list-box {
  overflow: auto;
  padding-bottom: 1rem;
}
.header-td {
  padding-left: 2rem;
}
.header-slot-default {
  @include truncate(false);
}
.header-td:first-child {
  border-top-left-radius: 0.7875rem;
}
.header-td:last-child {
  border-top-right-radius: 0.7875rem;
}
.no-rows-container {
  @include flex-center-justify;
  height: inherit;
}
.scrollEl {
  padding-top: 1rem;
}
.infinite-loader {
  padding: 0.25rem 0 0.75rem 0;
  height: 32px;
}
.title {
  text-align: left;
  height: 2.375rem;
  background-color: rgb(30, 32, 33);
  border-top-left-radius: 1.0625rem;
  border-top-right-radius: 1.0625rem;
  padding-top: 0.625rem;
  padding-left: 1.5rem;
  font-size: 1.125rem;
  font-family: $bold;
  color: $font-primary;
  padding: 0.4rem 0 0.7rem 1.25rem;
  caption-side: top;
}
</style>
